import type { ReactNode } from 'react';
import { Permission, UserRole } from '@klaro7/shared-types';

import { useAuth } from '../auth/hooks/useAuth';
import { usePermissions } from './usePermissions';

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  anyPermission?: Permission[];
  fallback?: ReactNode;
}

export function RoleGuard({
  children,
  allowedRoles,
  requiredPermission,
  requiredPermissions,
  anyPermission,
  fallback = null,
}: RoleGuardProps) {
  const { user, hasRole } = useAuth();
  const { can, canAll, canAny } = usePermissions();

  if (!user) {
    return <>{fallback}</>;
  }

  // Check roles
  if (allowedRoles && !hasRole(allowedRoles)) {
    return <>{fallback}</>;
  }

  // Check single permission
  if (requiredPermission && !can(requiredPermission)) {
    return <>{fallback}</>;
  }

  // Check all permissions
  if (requiredPermissions && !canAll(requiredPermissions)) {
    return <>{fallback}</>;
  }

  // Check any permission
  if (anyPermission && !canAny(anyPermission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
